'use client'

import { Button } from '@repo/ui/components/button'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@repo/ui/components/sheet'
import { Tag } from '@repo/ui/components/tag'
import { cn } from '@repo/ui/lib/utils'
import { createHrefWithQuery } from '@repo/url/href'
import { X } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { FunctionComponent, PropsWithChildren, ReactNode, useMemo } from 'react'
import { useRefinementUrl } from '../../../hooks/useRefinementUrl.hook'
import { SHEET, useSheet } from '../../../providers/SheetProvider'
import { CollectionDto } from '../../../types/collection.types'
import { PageDto } from '../../../types/page.types'

interface Props {
  pageDto: PageDto
  collectionDto: CollectionDto
  footer?: ReactNode
  className?: string
}

const CollectionFiltersSheet: FunctionComponent<PropsWithChildren<Props>> = ({ pageDto, collectionDto, className, children, footer }) => {
  const pathname = usePathname()
  const { openSheet, isSheetOpen, closeSheet } = useSheet()
  const { currentRefinements, deleteRefinement, count } = useRefinementUrl()
  const refinements = currentRefinements()

  const label = useMemo(
    () => (count > 0 ? pageDto.dictionary?.filter?.filterx || 'Filter: %1' : pageDto.dictionary?.filter?.filter || 'Filter').replace('%1', count.toString()),
    [count, pageDto.dictionary?.filter?.filter, pageDto.dictionary?.filter?.filterx],
  )

  const ariaLabel = useMemo(
    () => (pageDto.dictionary?.filter?.numberofactivefiltersx || 'Number of active filters: %1').replace('%1', count.toString()),
    [count, pageDto.dictionary?.filter?.numberofactivefiltersx],
  )

  const sanityOptions = useMemo(
    () => [collectionDto.collection?.brandOptions, collectionDto.collection?.styleOptions, collectionDto.collection?.colorFamilyOptions].flat(),
    [collectionDto.collection],
  )

  return (
    <>
      <Button className={cn(className)} onClick={() => openSheet(SHEET.FILTERS)}>
        {label}
      </Button>
      <Sheet open={isSheetOpen(SHEET.FILTERS)} onOpenChange={(open) => !open && closeSheet()}>
        <SheetContent side='right'>
          <SheetHeader>
            <SheetTitle>{label}</SheetTitle>
            <SheetDescription className='sr-only'>{ariaLabel}</SheetDescription>
          </SheetHeader>
          {refinements.length > 0 && (
            <section className='border-border animate-in fade-in w-full border-b border-solid py-3 duration-200'>
              <ul className='flex w-full snap-x scroll-pl-3 overflow-auto' role='list'>
                {refinements.flatMap((refinement) => {
                  return refinement.values.map((value) => (
                    <li key={`${refinement.property.id}-${value}`} className='flex snap-start first:ml-3 last:mr-3'>
                      <Tag asChild={true}>
                        <Link
                          href={createHrefWithQuery(pathname, deleteRefinement(refinement.property.id, value, false))}
                          aria-label={(pageDto.dictionary?.filter?.removexfilter || 'Remove %1 filter').replace('%1', `'${refinement.property.title}: ${value}'`)}
                        >
                          {sanityOptions?.find((sanityOption) => sanityOption?.urlSlug === value)?.title || pageDto.dictionary?.filter?.[value] || value}
                          <X className='relative -right-[0.15rem] top-[0.35rem]' />
                        </Link>
                      </Tag>
                    </li>
                  ))
                })}
              </ul>
            </section>
          )}
          <div className='max-h-[80svh] w-full overflow-y-auto overflow-x-hidden'>{children}</div>
          {footer && <SheetFooter className='border-border flex w-full flex-col items-start border-t border-solid'>{footer}</SheetFooter>}
        </SheetContent>
      </Sheet>
    </>
  )
}

export default CollectionFiltersSheet
